import React, { useRef, useState, useEffect, useCallback } from 'react';
import CalendarStrips from './CalendarStrips';
import { StartDateProvider, useStartDate } from './StartDateContext';
import { EditingProvider } from './EditingContext';
import { CalendarStripsProvider, useCalendarStrips } from './CalendarStripsContext';
import { useHasChanges } from './HasChangesContext';
import { usePlanName } from './PlanNameContext';
import { usePlanMetadata } from './PlanMetadataContext';
import { useModal } from './ModalContext';
import { useBatchOperations } from './BatchOperationsContext';
import { useCopyMove } from './CopyMoveContext';

// Import der neuen modularen Komponenten
import { Header, Sidebar, ResizeHandle, Modals, CopyMoveDialog } from './components';
import GlobalSyncModal from './components/GlobalSyncModal';
import PdfExportDialog from './components/PdfExportDialog';

// Import der Task-Assignment API-Funktionen
import { updateTaskAssignments, copyTaskAssignmentsToNewUUID, fetchTaskAssignments } from './api';
import config from './config';

// Import der Custom Hooks
import { useSaveLoad, useUndoRedo, useResize } from './hooks';
import { useLoginContext } from './LoginContext';

// Wrapper-Komponente für StartDate-Context
function ResizableLayoutContent({ left, right, header }) {
  const [visibleWeeks, setVisibleWeeks] = useState(50);
  const [weekWidth, setWeekWidth] = useState(200); // Neue State für Zoom
  const [isVisibleWeeksChanging, setIsVisibleWeeksChanging] = useState(false); // Tracking für Slider-Zustand
  const [isZoomChanging, setIsZoomChanging] = useState(false); // Tracking für Zoom-Änderungen
  const [hasOpenInfoPanels, setHasOpenInfoPanels] = useState(false); // Tracking für geöffnete InfoPanels
  const [showSchuelerModal, setShowSchuelerModal] = useState(false);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const panelGroupsContainerRef = useRef(null);
  const { calendarStripsRef } = useCalendarStrips();
  const { startDate, registerOnStartDateChange, setStartDate } = useStartDate();
  
  // Globale Überwachung aller InfoPanel-Status
  const taskStatusMapRef = useRef(new Map());
  const isClosingAllPanelsRef = useRef(false);
  const updateTimeoutRef = useRef(null);
  const clearAllDataRef = useRef(() => {});

  // Ref um Panel-Zustand vor PDF-Export zu merken
  const preExportPanelDataRef = useRef(null);

  // Öffne ALLE Panels (Hauptgruppen + Sub-Panels) für PDF-Export
  const openAllPanelsForExport = useCallback(() => {
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      if (containerData && containerData.groups) {
        // Merke aktuellen Zustand
        preExportPanelDataRef.current = JSON.parse(JSON.stringify(containerData));

        // Öffne ALLE Gruppen und ALLE Panels rekursiv
        const openAllRecursive = (panels) => {
          if (!panels) return panels;
          return panels.map(panel => {
            const openedPanel = { ...panel, open: true };
            if (panel.panels) {
              openedPanel.panels = openAllRecursive(panel.panels);
            }
            return openedPanel;
          });
        };

        const updatedGroups = containerData.groups.map(group => ({
          ...group,
          open: true,
          panels: openAllRecursive(group.panels)
        }));

        panelGroupsContainerRef.current.setData({
          ...containerData,
          groups: updatedGroups
        });
      }
    }
  }, []);

  // Stelle Panel-Zustand nach PDF-Export wieder her
  const restorePanelsAfterExport = useCallback(() => {
    if (panelGroupsContainerRef.current && preExportPanelDataRef.current) {
      panelGroupsContainerRef.current.setData(preExportPanelDataRef.current);
    }
    preExportPanelDataRef.current = null;
  }, []);

  useEffect(() => {
    const handleInfoPanelStatusChange = (event) => {
      // Ignoriere Events während des "Alle schließen" Vorgangs
      if (isClosingAllPanelsRef.current) {
        return;
      }
      
      const { taskId, hasOpenPanels, isCleanup } = event.detail;
      
      if (isCleanup) {
        // Task aus der Map entfernen
        taskStatusMapRef.current.delete(taskId);
      } else {
        // Aktualisiere die Status-Map
        taskStatusMapRef.current.set(taskId, hasOpenPanels);
      }
      
      // Debounce: Status-Updates sammeln und nur einmal ausführen
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      updateTimeoutRef.current = setTimeout(() => {
        const anyPanelsOpen = Array.from(taskStatusMapRef.current.values()).some(status => status === true);
        setHasOpenInfoPanels(anyPanelsOpen);
        updateTimeoutRef.current = null;
      }, 50); // Sammle Events für 50ms
    };

    window.addEventListener('infoPanelStatusChange', handleInfoPanelStatusChange);
    
    return () => {
      window.removeEventListener('infoPanelStatusChange', handleInfoPanelStatusChange);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);



  const handleVisibleWeeksChangeStart = () => {
    if (hasOpenInfoPanels) return; // Verhindere Änderung wenn InfoPanels offen sind
    setIsVisibleWeeksChanging(true);
  };

  const handleVisibleWeeksChangeEnd = (finalValue) => {
    setIsVisibleWeeksChanging(false);
    // Hier könnten wir zusätzliche Aktionen ausführen, wenn der Slider-Vorgang abgeschlossen ist
  };

  const handleZoomChangeStart = () => {
    if (hasOpenInfoPanels) return; // Verhindere Änderung wenn InfoPanels offen sind
    setIsZoomChanging(true);
  };

  const handleZoomChangeEnd = (finalValue) => {
    setIsZoomChanging(false);
    // Hier könnten wir zusätzliche Aktionen ausführen, wenn der Zoom-Vorgang abgeschlossen ist
  };
  
  const handleInfoPanelsChange = (hasOpenPanels) => {
    // Diese Funktion wird jetzt nicht mehr verwendet, da wir globale Events nutzen
    // Behalten wir für Backward-Compatibility
  };
  
  const handleCloseAllInfoPanels = () => {
    // Setze Flag um Events während des Schließen-Vorgangs zu ignorieren
    isClosingAllPanelsRef.current = true;
    
    // Sofort den Status setzen
    setHasOpenInfoPanels(false);
    taskStatusMapRef.current.clear();
    
    // Alle InfoPanels in allen Task-Komponenten schließen
    window.dispatchEvent(new CustomEvent('closeAllInfoPanels'));
    
    // Flag nach einer Verzögerung zurücksetzen
    setTimeout(() => {
      isClosingAllPanelsRef.current = false;
    }, 200); // Genug Zeit für alle Komponenten um zu reagieren
  };
  
  // Prüft, ob alle Panels geschlossen sind
  const areAllPanelsClosed = () => {
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      if (containerData && containerData.groups) {
        return containerData.groups.every(group => group.open === false);
      }
    }
    return false;
  };

  // Toggle zwischen Collapse All und Expand All
  const handleToggleAllPanels = () => {
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      if (containerData && containerData.groups) {
        // Prüfe aktuellen Zustand aller Gruppen
        const hasAnyOpenGroup = containerData.groups.some(group => group.open === true);
        
        // Einfache Logik:
        // Wenn mindestens eine Gruppe offen ist -> schließe alle (open = false)
        // Wenn alle Gruppen geschlossen sind -> öffne alle (open = true)
        const targetOpenState = !hasAnyOpenGroup;
        
        const updatedGroups = containerData.groups.map(group => ({
          ...group,
          open: targetOpenState
        }));
        
        // Verwende forceSetData um die Erhaltungslogik zu umgehen
        panelGroupsContainerRef.current.forceSetData({
          ...containerData,
          groups: updatedGroups
        });
      }
    }
  };

  // Schließe alle Panels (für Startdatum-Änderungen)
  // eslint-disable-next-line no-unused-vars
  const handleCloseAllPanels = () => {
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      if (containerData && containerData.groups) {
        const updatedGroups = containerData.groups.map(group => ({
          ...group,
          open: false
        }));
        
        panelGroupsContainerRef.current.setData({
          ...containerData,
          groups: updatedGroups
        });
      }
    }
  };

  // Öffne alle Panels (für nach Startdatum-Änderungen)
  const handleOpenAllPanels = useCallback(() => {
    if (panelGroupsContainerRef.current) {
      const containerData = panelGroupsContainerRef.current.getData();
      if (containerData && containerData.groups) {
        const updatedGroups = containerData.groups.map(group => ({
          ...group,
          open: true
        }));
        
        panelGroupsContainerRef.current.setData({
          ...containerData,
          groups: updatedGroups
        });
        
        // Nach dem Öffnen aller Panels, löse eine globale Aktualisierung aus
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('forceTasksUpdate'));
        }, 150); // Verzögerung um sicherzustellen, dass Panel-Öffnung abgeschlossen ist
      }
    }
  }, []);

  // Registriere den Callback für Startdatum-Änderungen
  useEffect(() => {
    registerOnStartDateChange((newDate) => {
      // Sende Event um Panel-Zustände zu speichern und alle zu schließen
      window.dispatchEvent(new CustomEvent('saveAndCloseAllCollapsiblePanels'));
      
      // Stelle den vorherigen Zustand nach einer kurzen Verzögerung wieder her
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('restoreCollapsiblePanels'));
      }, 100);
    });
  }, [registerOnStartDateChange]);

  // State für Duplikat-Erkennung
  const [hasDuplicates, setHasDuplicates] = useState(false);

  // Funktion zum Prüfen, ob Duplikate vorhanden sind
  const checkForDuplicates = useCallback(() => {
    if (panelGroupsContainerRef.current && panelGroupsContainerRef.current.checkForDuplicates) {
      // Prüfe sowohl Gruppe- als auch Panel-Duplikate
      const result = panelGroupsContainerRef.current.checkForDuplicates(true);
      const hasAnyDuplicates = result.hasGroupDuplicates || result.hasPanelDuplicates;
      setHasDuplicates(hasAnyDuplicates);
    } else {
      setHasDuplicates(false);
    }
  }, []);

  // Verwende die Custom Hooks
  const loginData = useLoginContext();
  const undoRedoData = useUndoRedo(panelGroupsContainerRef, 20, checkForDuplicates);
  
  // Verwende die existierende handleLoadFromDatabase Funktion direkt
  const resizeData = useResize();
  const headerRef = useRef(null);
  
  // Batch Operations Hook
  const batchOperations = useBatchOperations();
  
  // CopyMove Hook
  const copyMove = useCopyMove();

  // Plan-Management States  
  const { hasChanges } = useHasChanges();
  const { updatePlanName, currentPlanName } = usePlanName();
  const { planMetadata, updatePlanMetadata, clearPlanMetadata } = usePlanMetadata();
  const { showConfirm, showAlert } = useModal();

  const saveLoadData = useSaveLoad(panelGroupsContainerRef, loginData.user, handleOpenAllPanels, visibleWeeks, checkForDuplicates, undoRedoData);
  
  // Funktion zum Laden von Lernenden-Plänen
  const handleLoadStudentPlan = useCallback(async (studentPlan, skipConfirmation = false) => {
    try {
      console.log('Lade Lernenden-Plan:', studentPlan.name);
      
      // Prüfe ob bereits Plan-Daten vorhanden sind
      const hasExistingData = () => {
        if (!panelGroupsContainerRef.current) return false;
        const currentData = panelGroupsContainerRef.current.getData();
        return currentData && currentData.groups && currentData.groups.length > 0;
      };

      if (hasExistingData() && !skipConfirmation) {
        // Zeige Bestätigungsdialog nur wenn nicht übersprungen
        const shouldReplace = await showConfirm({
          title: 'Plan ersetzen',
          message: `Möchten Sie den aktuellen Plan durch "${studentPlan.name}" ersetzen?\n\nAlle nicht gespeicherten Änderungen gehen verloren.`,
          confirmText: 'Ersetzen',
          cancelText: 'Abbrechen'
        });
        
        if (!shouldReplace) {
          return; // Abbruch
        }
      }

      // Plan-Daten laden
      console.log('StudentPlan structure check:', {
        hasData: !!studentPlan.data,
        hasPanelGroups: !!(studentPlan.data && studentPlan.data.panelGroups),
        studentPlan: studentPlan
      });
      
      if (studentPlan.data && studentPlan.data.panelGroups) {
        // Setze neue Plan-Metadaten
        updatePlanMetadata({
          planName: studentPlan.name,
          planVersion: studentPlan.version,
          planId: studentPlan.id,
          baseName: studentPlan.base_name,
          isStudentPlan: true, // Markierung für Lernenden-Plan
          isOverviewPlan: studentPlan.merged || false, // Markierung für Übersichtsplan
          sourcePlans: studentPlan.sourcePlans || []
        });
        
        // Aktualisiere Plan-Namen
        updatePlanName(studentPlan.name);
        
        // Setze Startdatum falls vorhanden
        if (studentPlan.data.startDate) {
          setStartDate(new Date(studentPlan.data.startDate));
        }
        
        // Lade Panel-Daten
        if (panelGroupsContainerRef.current) {
          panelGroupsContainerRef.current.setData({
            groups: studentPlan.data.panelGroups || []
          });
        }

        // Öffne alle Panels nach dem Laden
        setTimeout(() => {
          handleOpenAllPanels();
        }, 200);

        if (!skipConfirmation) {
          try {
            console.log('Zeige Success Alert...');
            // Erstelle eine einfache, sichere Erfolgs-Nachricht
            const title = "Plan geladen";
            const successMessage = "Der Plan wurde erfolgreich geladen!";
            
            console.log('Alert wird aufgerufen mit:', { title, successMessage });
            await showAlert({ message: successMessage, title });
            console.log('Alert wurde erfolgreich angezeigt');
          } catch (alertError) {
            console.error('Fehler beim Anzeigen des Alerts:', alertError);
          }
        }
      } else {
        console.log('Plan-Daten nicht verfügbar, zeige Fehler-Alert');
        await showAlert('Plan-Daten sind nicht verfügbar oder fehlerhaft.', {
          title: "Fehler"
        });
      }
    } catch (error) {
      console.error('Fehler beim Laden des Lernenden-Plans:', error);
      await showAlert('Fehler beim Laden des Plans: ' + error.message, {
        title: "Fehler"
      });
    }
  }, [showConfirm, showAlert, updatePlanMetadata, updatePlanName, setStartDate, handleOpenAllPanels]);

  // Event-Listener für automatisches Laden bei Lernenden-Login
  React.useEffect(() => {
    const handleStudentLogin = async (event) => {
      const { user, plans, shouldAutoLoadFirstPlan } = event.detail;
      
      if (shouldAutoLoadFirstPlan && plans.length > 0) {
        try {
          // Lade alle Pläne und erstelle Übersichtsplan
          const { fetchStudentPlan } = await import('./api');
          const { mergePlansToOverview } = await import('./utils/planMerger');
          
          // Alle Pläne laden
          const planPromises = plans.map(plan => 
            fetchStudentPlan(plan.id, user.username)
          );
          
          const results = await Promise.all(planPromises);
          
          // Nur erfolgreiche Ergebnisse verwenden
          const loadedPlans = results
            .filter(result => result.success && result.plan)
            .map(result => result.plan);
          
          if (loadedPlans.length > 0) {
            // Pläne zu Übersichtsplan zusammenführen
            const overviewPlan = mergePlansToOverview(loadedPlans);
            
            // Direkte Verwendung der Plan-Loading-Logik
            try {
              const studentPlan = overviewPlan;
              
              // Plan-Daten laden
              if (studentPlan.data && studentPlan.data.panelGroups) {
                // Setze neue Plan-Metadaten
                updatePlanMetadata({
                  planName: studentPlan.name,
                  planVersion: studentPlan.version,
                  planId: studentPlan.id,
                  baseName: studentPlan.base_name,
                  isStudentPlan: true, // Markierung für Lernenden-Plan
                  isOverviewPlan: studentPlan.merged || false, // Markierung für Übersichtsplan
                  sourcePlans: studentPlan.sourcePlans || []
                });
                
                // Aktualisiere Plan-Namen
                updatePlanName(studentPlan.name);
                
                // Setze Startdatum falls vorhanden
                if (studentPlan.data.startDate) {
                  setStartDate(new Date(studentPlan.data.startDate));
                }
                
                // Lade Panel-Daten
                if (panelGroupsContainerRef.current) {
                  panelGroupsContainerRef.current.setData({
                    groups: studentPlan.data.panelGroups || []
                  });
                }

                // Öffne alle Panels nach dem Laden
                setTimeout(() => {
                  handleOpenAllPanels();
                }, 200);

              }
            } catch (error) {
              console.error('Fehler beim automatischen Laden des Plans:', error);
            }
          } else {
            console.log('Keine Pläne konnten geladen werden für automatischen Übersichtsplan');
          }
        } catch (error) {
          console.error('Fehler beim Laden der Lernenden-Pläne für Übersichtsplan:', error);
        }
      }
    };

    // Event-Listener für Logout - alle Plan-Daten löschen
    const handleUserLogout = () => {
      console.log('Benutzer abgemeldet - lösche alle Plan-Daten aus dem Speicher');
      // eslint-disable-next-line no-use-before-define
      clearAllDataRef.current();
    };

    window.addEventListener('studentLoggedIn', handleStudentLogin);
    window.addEventListener('userLogout', handleUserLogout);
    
    return () => {
      window.removeEventListener('studentLoggedIn', handleStudentLogin);
      window.removeEventListener('userLogout', handleUserLogout);
    };
  }, [updatePlanMetadata, updatePlanName, setStartDate, handleOpenAllPanels, saveLoadData, currentPlanName]);
  
  // Schüler Modal Daten
  const schuelerData = {
    showSchuelerModal,
    setShowSchuelerModal
  };

  // Hilfsfunktion: Prüft ob es sich um einen neuen, ungespeicherten Plan handelt
  const isNewUnsavedPlan = useCallback(() => {
    // Da "unbenannt" nicht als gültiger Planname gespeichert werden kann,
    // ist jeder Plan mit diesem Namen definitiv ein neuer, ungespeicherter Plan
    return currentPlanName === 'unbenannt';
  }, [currentPlanName]);

  // Keyboard Event Handler für Ctrl+S
  React.useEffect(() => {
    const handleKeyDown = async (event) => {
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        
        // Prüfe ob es sich um einen neuen, ungespeicherten Plan handelt
        if (isNewUnsavedPlan()) {
          // Bei neuen Plänen: Öffne "Speichern als" Dialog
          if (!loginData.user || !loginData.user.username) {
            // Nutzer muss angemeldet sein
            await showConfirm({
              title: 'Anmeldung erforderlich',
              message: 'Sie müssen angemeldet sein, um den Plan zu speichern.',
              confirmText: 'OK',
              cancelText: '' // Nur OK Button
            });
            return;
          }
          
          // Öffne "Speichern als" Dialog
          saveLoadData.handleDatabaseSave();
        } else {
          // Bei bestehenden Plänen: Normale Ctrl+S Logik
          if (saveLoadData.handleCtrlSave) {
            const result = await saveLoadData.handleCtrlSave();
            // Zeige Bestätigung nur bei erfolgreichem Speichern
            if (result && result.success && headerRef.current) {
              headerRef.current.showCtrlSaveConfirmation();
            }
            // Bei Fehlern wird bereits in handleCtrlSave eine Warnung angezeigt
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [saveLoadData, isNewUnsavedPlan, loginData.user, showConfirm]);

  // Funktion zum Löschen aller Daten
  const clearAllData = useCallback(() => {
    // Setze Start-Datum auf heute
    const today = new Date();
    const mondayOffset = today.getDay() === 0 ? -6 : 1 - today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    if (panelGroupsContainerRef.current) {
      // Leere alle Panel-Gruppen
      panelGroupsContainerRef.current.setData({ groups: [] });
    }

    // Setze Startdatum zurück
    setStartDate(monday);

    // Leere Plan-Metadaten
    clearPlanMetadata();

    // Setze Plan-Namen auf Standard zurück
    updatePlanName('unbenannt');

    // Deaktiviere geladenen Plan-Status in der Sidebar
    if (saveLoadData.setCurrentLoadedPlanId) {
      saveLoadData.setCurrentLoadedPlanId(null);
    }
    if (saveLoadData.setCurrentLoadedPlanVersion) {
      saveLoadData.setCurrentLoadedPlanVersion(null);
    }

    // Lösche alle localStorage-Einträge für Plan-Sessions beim Logout
    // Da wir alle Plan-Daten löschen möchten, löschen wir alle plan_saved_ und plan_id_ Einträge
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('plan_saved_') || key.startsWith('plan_id_'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setStartDate, clearPlanMetadata, updatePlanName, saveLoadData.setCurrentLoadedPlanId, saveLoadData.setCurrentLoadedPlanVersion]);

  // Aktualisiere die Ref, damit useEffect und andere Stellen darauf zugreifen können
  clearAllDataRef.current = clearAllData;

  // Hauptfunktion für "Neuen Plan erstellen"
  const handleCreateNewPlan = async () => {
    // 1. Prüfe auf ungespeicherte Änderungen und zeige Warnung
    if (hasChanges) {
      const shouldSave = await showConfirm({
        title: 'Ungespeicherte Änderungen',
        message: 'Es gibt ungespeicherte Änderungen. Möchten Sie diese vor dem Erstellen eines neuen Plans speichern?',
        confirmText: 'Speichern',
        cancelText: 'Nicht speichern'
      });

      if (shouldSave) {
        // Verwende die gleiche Funktion wie CTRL+S
        if (saveLoadData.handleCtrlSave) {
          const result = await saveLoadData.handleCtrlSave();
          if (result && result.success && headerRef.current) {
            headerRef.current.showCtrlSaveConfirmation();
          }
          // Falls Speichern fehlschlägt, brich das Erstellen des neuen Plans ab
          if (!result || !result.success) {
            return; // Abbruch
          }
        }
      }
    }

    // 2. Lösche aktuellen Plan aus dem Speicher
    clearAllDataRef.current();

    // 3. Setze leeren Plan auf "unbenannt"
    updatePlanName('unbenannt');
    
    // 4. Setze neue Plan-Metadaten für neuen Plan
    updatePlanMetadata({
      planName: 'unbenannt',
      planVersion: 1,
      planId: null,
      baseName: 'unbenannt'
    });
    
    // 5. Deaktiviere alle als aktiv markierten Pläne in der Sidebar
    if (saveLoadData.setCurrentLoadedPlanId) {
      saveLoadData.setCurrentLoadedPlanId(null);
    }
    
    // 6. Zeige Hinweis zum Speichern des neuen Plans
    await showAlert({
      title: "Neuer Plan",
      message: 'Neuer Plan erstellt!\n\nKlicken Sie auf "Speichern als" um den neuen Plan zu benennen und zu speichern.'
    });
  };



  // States für das Merge-Modal
  const [showMergeModal, setShowMergeModal] = useState(false);

  // States für das Global Sync Modal
  const [showGlobalSyncModal, setShowGlobalSyncModal] = useState(false);

  // Initiale Prüfung bei Komponenten-Mount
  React.useEffect(() => {
    const initialCheck = () => {
      checkForDuplicates();
    };
    
    // Kurze Verzögerung, damit der Container initialisiert ist
    const timer = setTimeout(initialCheck, 500);
    return () => clearTimeout(timer);
  }, [checkForDuplicates]);

  // Funktion zum Zusammenführen doppelter Panelgruppen
  const handleMergeDuplicateGroups = async () => {
    setShowMergeModal(true);
  };

  // Funktion für die globale Synchronisation
  const handleGlobalSync = async () => {
    // Prüfe ob Plan gespeichert werden muss
    if (currentPlanName === 'unbenannt') {
      await showAlert(
        'Bevor Sie Lernende synchronisieren können, muss der Plan gespeichert werden.\n\nBitte speichern Sie Ihren Plan zuerst (Ctrl+S oder "Speichern als").',
        { title: "Plan speichern erforderlich" }
      );
      return;
    }
    
    setShowGlobalSyncModal(true);
  };

  // Hilfsfunktion für das Sanitizing der Klassennamen
  const sanitizeKlassensynchronisation = (value) => {
    if (!value) return '';
    // Entferne Whitespaces vor und nach Kommas, und entferne leere Einträge
    return value
      .split(',')
      .map(klasse => klasse.trim())
      .filter(klasse => klasse.length > 0)
      .join(',');
  };

  // Globale Synchronisations-Funktion
  const handleGlobalSyncConfirm = async (selectedSchuljahr, alleLernende, removeUnmatchedStudents = false) => {
    try {
      if (!panelGroupsContainerRef.current) {
        throw new Error('Panel-Container nicht verfügbar');
      }

      const containerData = panelGroupsContainerRef.current.getData();
      if (!containerData || !containerData.groups) {
        throw new Error('Keine Panel-Daten verfügbar');
      }

      // Prüfe ob Login-Daten und Plan-Metadaten verfügbar sind
      if (!loginData.user || !planMetadata.planName) {
        throw new Error('Anmeldung oder Plan-Informationen fehlen für die Synchronisation');
      }

      // Debug: Log plan metadata values
      console.log('Global Sync - Plan Metadata:', {
        planName: planMetadata.planName,
        planVersion: planMetadata.planVersion,
        planId: planMetadata.planId,
        baseName: planMetadata.baseName
      });

      // Initialize plan version variables
      let actualPlanVersion = planMetadata.planVersion;
      let actualPlanName = planMetadata.planName;

      // Prüfe ob planMetadata vollständig geladen ist
      if (!planMetadata.planVersion || planMetadata.planVersion === 1) {
        // Wenn planVersion nicht verfügbar ist, versuche es über die planId zu laden
        if (planMetadata.planId) {
          console.log('Plan metadata incomplete, fetching current plan version via API...');
          
          try {
            const response = await fetch(`${config.API_HOST}:${config.API_PORT}/api/plans/${planMetadata.planId}`, {
              headers: {
                'x-api-key': config.API_KEY,
                'x-user-id': loginData.user?.username || loginData.user?.login || loginData.user?.id || 'default-user'
              }
            });
            
            if (response.ok) {
              const planResult = await response.json();
              if (planResult.success && planResult.plan) {
                // Aktualisiere die Plan-Metadaten für zukünftige Verwendung
                updatePlanMetadata({
                  planName: planResult.plan.name,
                  planVersion: planResult.plan.version,
                  planId: planResult.plan.id,
                  baseName: planResult.plan.base_name
                });
                
                console.log(`Updated plan metadata: version ${planResult.plan.version} for plan "${planResult.plan.name}"`);
                
                // Verwende die aktuellen Werte für diese Synchronisation
                actualPlanVersion = planResult.plan.version;
                actualPlanName = planResult.plan.name;
              }
            }
          } catch (error) {
            console.warn('Could not fetch current plan version:', error.message);
            throw new Error(`Plan-Version konnte nicht ermittelt werden. Laden Sie den Plan erneut oder kontaktieren Sie den Support. (${error.message})`);
          }
        } else {
          throw new Error('Plan-Informationen sind unvollständig. Laden Sie den Plan erneut.');
        }
      }

      let totalSyncedTasks = 0;
      let totalAssignedStudents = 0;
      let totalRemovedStudents = 0;
      let tasksWithoutSync = 0;
      let tasksWithoutUuid = 0;
      const syncErrors = [];

      // Schritt 0: Sammle alle aktuellen Task-UUIDs aus dem Plan
      const currentTaskUUIDs = new Set();
      const collectTaskUUIDs = (obj) => {
        if (Array.isArray(obj)) {
          obj.forEach(collectTaskUUIDs);
        } else if (obj && typeof obj === 'object') {
          if (obj.uuid && typeof obj.uuid === 'string' && obj.uuid.startsWith('task-')) {
            currentTaskUUIDs.add(obj.uuid);
          }
          Object.values(obj).forEach(collectTaskUUIDs);
        }
      };
      
      collectTaskUUIDs(containerData);
      console.log(`Gefundene Task-UUIDs im aktuellen Plan: ${currentTaskUUIDs.size}`);
      
      // Schritt 0.1: Lösche verwaiste Zuordnungen (Tasks die nicht mehr im Plan existieren)
      if (currentTaskUUIDs.size > 0) {
        try {
          // Lade alle aktuellen Zuordnungen für diesen Plan
          const allAssignmentsResponse = await fetch(`${config.API_HOST}:${config.API_PORT}/api/plan-assignments/${encodeURIComponent(actualPlanName || planMetadata.planName)}/${actualPlanVersion || planMetadata.planVersion || 1}`, {
            headers: {
              'x-api-key': config.API_KEY,
              'x-user-id': loginData.user?.username || loginData.user?.login || loginData.user?.id || 'default-user'
            }
          });
          
          if (allAssignmentsResponse.ok) {
            const allAssignmentsResult = await allAssignmentsResponse.json();
            const allAssignments = allAssignmentsResult.assignments || [];
            
            // Finde verwaiste Task-UUIDs (in DB aber nicht im aktuellen Plan)
            const orphanedTaskUUIDs = new Set();
            allAssignments.forEach(assignment => {
              if (!currentTaskUUIDs.has(assignment.taskUuid)) {
                orphanedTaskUUIDs.add(assignment.taskUuid);
              }
            });
            
            if (orphanedTaskUUIDs.size > 0) {
              console.log(`Gefundene verwaiste Task-UUIDs: ${orphanedTaskUUIDs.size} - ${Array.from(orphanedTaskUUIDs).join(', ')}`);
              
              // Lösche Zuordnungen für verwaiste Tasks
              let totalOrphansRemoved = 0;
              for (const orphanedUUID of orphanedTaskUUIDs) {
                try {
                  const orphanAssignments = allAssignments.filter(a => a.taskUuid === orphanedUUID);
                  
                  // Verwende updateTaskAssignments mit leerer Liste um alle Zuordnungen für diesen Task zu löschen
                  const deleteResult = await updateTaskAssignments(
                    loginData.user?.username || loginData.user?.login || loginData.user?.id || 'default-user',
                    actualPlanName || planMetadata.planName,
                    actualPlanVersion || planMetadata.planVersion || 1,
                    orphanedUUID,
                    [] // Leere Liste = alle Zuordnungen löschen
                  );
                  
                  if (!deleteResult.error) {
                    totalOrphansRemoved += orphanAssignments.length;
                    console.log(`Entfernt: ${orphanAssignments.length} verwaiste Zuordnungen für Task ${orphanedUUID}`);
                  }
                } catch (error) {
                  console.warn(`Fehler beim Entfernen verwaister Zuordnungen für ${orphanedUUID}:`, error.message);
                }
              }
              
              if (totalOrphansRemoved > 0) {
                console.log(`✅ Insgesamt ${totalOrphansRemoved} verwaiste Zuordnungen entfernt`);
              }
            }
          }
        } catch (error) {
          console.warn('Fehler beim Bereinigen verwaister Zuordnungen:', error.message);
          // Fehler nicht als fatal behandeln, Synchronisation kann fortgesetzt werden
        }
      }

      // Hilfsfunktion zur Task-Synchronisation mit Datenbankpersistierung
      const syncTaskWithDatabase = async (bar, gefilterteLernende, currentAssignedStudents) => {
        // Prüfe ob Task eine UUID hat - für Legacy-Tasks ohne UUID keine DB-Speicherung möglich
        if (!bar.uuid) {
          tasksWithoutUuid++;
          console.warn('Task ohne UUID gefunden - keine Datenbankpersistierung möglich:', bar.id);
          return false;
        }

        try {
          // Bestimme welche Studenten zugewiesen werden sollen
          let finalStudents = [];
          let studentsAdded = 0;
          let studentsRemoved = 0;

          if (removeUnmatchedStudents) {
            // Vollständiger Ersatz: Nur Studenten aus den definierten Klassen
            finalStudents = [...gefilterteLernende];
            studentsRemoved = Math.max(0, currentAssignedStudents.length - finalStudents.length);
          } else {
            // Hinzufügen-Modus: Bestehende + neue Studenten (ohne Duplikate)
            // WICHTIG: Da das Backend ein vollständiges Replace macht (DELETE + INSERT),
            // müssen wir hier die finale Liste bestimmen, nicht versuchen hinzuzufügen
            const existingLogins = new Set(currentAssignedStudents.map(s => s.login));
            const classStudentLogins = new Set(gefilterteLernende.map(s => s.login));
            
            // Kombiniere: bestehende Studenten (die nicht aus den Klassen stammen) + alle Studenten aus den Klassen
            const studentsNotFromClasses = currentAssignedStudents.filter(s => !classStudentLogins.has(s.login));
            finalStudents = [...studentsNotFromClasses, ...gefilterteLernende];
            
            // Berechne wie viele neue Studenten hinzugefügt werden
            studentsAdded = gefilterteLernende.filter(s => !existingLogins.has(s.login)).length;
          }

          // Transformiere Studentendaten für Backend-Kompatibilität
          const transformedStudents = finalStudents.map(student => ({
            login: student.login,
            vorname: student.vorname,
            nachname: student.nachname,
            schuljahrId: student.schuljahrId || selectedSchuljahr?.id || 1,
            klasse: student.klasse,
            klassenId: student.klasseId || student.klassenId || null
          }));

          // Speichere Zuordnungen in der Datenbank
          // Verwende immer updateTaskAssignments (vollständiger Ersatz), da wir die finale Liste bereits bestimmt haben
          const result = await updateTaskAssignments(
            loginData.user?.username || loginData.user?.login || loginData.user?.id || 'default-user',
            actualPlanName || planMetadata.planName,
            actualPlanVersion || planMetadata.planVersion || 1,
            bar.uuid,
            transformedStudents
          );

          if (result.error) {
            syncErrors.push(`Task ${bar.uuid}: ${result.error}`);
            return false;
          }

          // Lokalen State aktualisieren nur bei erfolgreichem DB-Update
          bar.assignedStudents = finalStudents;
          
          // Zähle Änderungen
          if (removeUnmatchedStudents) {
            totalRemovedStudents += studentsRemoved;
            totalAssignedStudents += finalStudents.length - currentAssignedStudents.length + studentsRemoved;
          } else {
            totalAssignedStudents += studentsAdded;
          }

          return true;

        } catch (error) {
          console.error(`Fehler bei der Synchronisation von Task ${bar.uuid}:`, error);
          syncErrors.push(`Task ${bar.uuid}: ${error.message}`);
          return false;
        }
      };

      // Durchsuche alle Tasks und synchronisiere sie
      const syncTasksInPanel = async (panel) => {
        if (panel.movableBars && Array.isArray(panel.movableBars)) {
          for (const bar of panel.movableBars) {
            // Prüfe ob Task eine Klassensynchronisation hat
            if (bar.klassensynchronisation) {
              const sanitizedKlassenNamen = sanitizeKlassensynchronisation(bar.klassensynchronisation);
              if (sanitizedKlassenNamen) {
                const klassenNamen = sanitizedKlassenNamen.split(',');
                
                // Filtere Lernende nach übereinstimmenden Klassennamen
                const gefilterteLernende = alleLernende.filter(lernender => 
                  klassenNamen.some(klasse => 
                    lernender.klasse && lernender.klasse.toLowerCase() === klasse.toLowerCase()
                  )
                );

                // Initialisiere zugewiesene Lernende falls nicht vorhanden
                if (!bar.assignedStudents) {
                  bar.assignedStudents = [];
                }

                const currentAssignedStudents = bar.assignedStudents || [];
                
                // Synchronisiere Task mit Datenbankpersistierung
                const success = await syncTaskWithDatabase(bar, gefilterteLernende, currentAssignedStudents);
                
                if (success) {
                  totalSyncedTasks++;
                }
              }
            } else {
              tasksWithoutSync++;
            }
          }
        }
        
        // Rekursiv für Untergruppen
        if (panel.type === 'group' && panel.panels) {
          for (const subPanel of panel.panels) {
            await syncTasksInPanel(subPanel);
          }
        }
      };

      // Synchronisiere alle Gruppen sequenziell
      for (const group of containerData.groups) {
        if (group.panels) {
          for (const panel of group.panels) {
            await syncTasksInPanel(panel);
          }
        }
      }

      // Aktualisiere die Container-Daten
      if (totalSyncedTasks > 0) {
        panelGroupsContainerRef.current.setData(containerData);
      }

      // Erstelle Rückgabe-Nachricht
      let message = '';
      const messageParts = [];

      if (totalSyncedTasks === 0) {
        if (tasksWithoutUuid > 0 && tasksWithoutSync === 0) {
          message = `${tasksWithoutUuid} Tasks ohne UUID gefunden - keine Datenbankpersistierung möglich. Aktualisieren Sie diese Tasks um UUIDs hinzuzufügen.`;
        } else if (tasksWithoutSync > 0) {
          message = 'Keine Tasks mit Klassensynchronisation gefunden.';
        } else {
          message = 'Keine synchronisierbaren Tasks gefunden.';
        }
      } else {
        messageParts.push(`${totalSyncedTasks} Tasks erfolgreich synchronisiert`);
        
        if (totalAssignedStudents > 0) {
          messageParts.push(`${totalAssignedStudents} Lernende zugewiesen`);
        }
        if (totalRemovedStudents > 0) {
          messageParts.push(`${totalRemovedStudents} Lernende entfernt`);
        }
        
        message = `Synchronisation abgeschlossen! ${messageParts.join(', ')}.`;
        
        if (tasksWithoutUuid > 0) {
          message += ` (${tasksWithoutUuid} Legacy-Tasks ohne UUID übersprungen)`;
        }
        
        if (syncErrors.length > 0) {
          message += `\n\nFehler bei ${syncErrors.length} Tasks: ${syncErrors.join(', ')}`;
        }
      }

      return {
        success: true,
        totalSyncedTasks,
        totalAssignedStudents,
        totalRemovedStudents,
        tasksWithoutSync,
        tasksWithoutUuid,
        syncErrors,
        message
      };

    } catch (error) {
      console.error('Fehler bei der globalen Synchronisation:', error);
      return {
        success: false,
        message: error.message || 'Unbekannter Fehler bei der Synchronisation'
      };
    }
  };

  // Callback für das Merge-Modal
  const handleMergeConfirm = async (option) => {
    if (panelGroupsContainerRef.current && panelGroupsContainerRef.current.mergeDuplicateGroups) {
      const includePanels = option === 'both';
      const result = panelGroupsContainerRef.current.mergeDuplicateGroups(includePanels);
      
      if (result.groupCount > 0 || result.panelCount > 0) {
        let message = '';
        if (result.groupCount > 0 && result.panelCount > 0) {
          message = `Es wurden ${result.groupCount} Panelgruppen und ${result.panelCount} Panels mit doppelten Namen zusammengeführt.`;
        } else if (result.groupCount > 0) {
          message = `Es wurden ${result.groupCount} Panelgruppen mit doppelten Namen zusammengeführt.`;
        } else if (result.panelCount > 0) {
          message = `Es wurden ${result.panelCount} Panels mit doppelten Namen zusammengeführt.`;
        }
        
        await showConfirm({
          title: 'Zusammenführung erfolgreich',
          message: message,
          confirmText: 'OK',
          showCancel: false
        });
        
        // Nach dem Zusammenführen prüfen, ob noch Duplikate vorhanden sind
        checkForDuplicates();
      } else {
        const searchType = includePanels ? 'Panelgruppen und Panels' : 'Panelgruppen';
        await showConfirm({
          title: 'Keine Duplikate gefunden',
          message: `Es wurden keine ${searchType} mit identischen Namen gefunden.`,
          confirmText: 'OK',
          showCancel: false
        });
      }
    }
  };

  // Neue Plan Daten (nicht mehr benötigt - direkte Implementierung)
  const newPlanData = null;

  // Merge-Daten
  const mergeData = {
    showMergeModal,
    setShowMergeModal,
    handleMergeConfirm
  };

  // Funktion zum Sammeln aller Tasks aus den Panel-Daten
  const getAllTasks = () => {
    if (!panelGroupsContainerRef.current) return [];
    
    const containerData = panelGroupsContainerRef.current.getData();
    if (!containerData || !containerData.groups) return [];
    
    const tasks = [];
    
    const extractTasksFromPanels = (panels, panelPath = '', groupTitle = '') => {
      if (!Array.isArray(panels)) return;
      
      panels.forEach((panel, panelIndex) => {
        const currentPath = panelPath ? `${panelPath} > ${panel.title}` : panel.title;
        
        if (panel.type === 'group' && panel.panels) {
          // Rekursiv Untergruppen durchsuchen
          extractTasksFromPanels(panel.panels, currentPath, groupTitle);
        } else if (panel.movableBars && Array.isArray(panel.movableBars)) {
          // Tasks aus movableBars extrahieren
          panel.movableBars.forEach((bar, barIndex) => {
            const taskData = {
              id: `${panel.id}_${bar.id}`, // Eindeutige ID basierend auf Panel + Bar
              barId: bar.id,
              panelId: panel.id,
              panelTitle: panel.title || 'Unbenanntes Panel',
              panelGroupTitle: groupTitle || 'Unbenannte Gruppe',
              panelPath: currentPath,
              label: bar.label || 'Unbenannter Task',
              startDate: bar.startDate ? new Date(bar.startDate) : null,
              endDate: bar.endDate ? new Date(bar.endDate) : null,
              textColor: bar.textColor,
              backgroundColor: bar.backgroundColor,
              borderStyle: bar.borderStyle,
              borderColor: bar.borderColor,
              borderThickness: bar.borderThickness,
              linkEvidence: bar.linkEvidence,
              evidenceType: bar.evidenceType,
              evidenceLabel: bar.evidenceLabel,
              evidenceDueDays: bar.evidenceDueDays,
              evidenceData: bar.evidenceData,
              assignedStudents: bar.assignedStudents || [],
              x: bar.x,
              y: bar.y,
              width: bar.width
            };
            
            tasks.push(taskData);
          });
        }
      });
    };
    
    containerData.groups.forEach(group => {
      extractTasksFromPanels(group.panels || [], '', group.title || 'Unbenannte Gruppe');
    });
    
    return tasks;
  };

  // Funktion zum Sammeln aller Panels (auch leere) aus den Panel-Daten
  const getAllPanels = () => {
    if (!panelGroupsContainerRef.current) return [];
    
    const containerData = panelGroupsContainerRef.current.getData();
    if (!containerData || !containerData.groups) return [];
    
    const panels = [];
    
    const extractPanelsFromGroups = (panelsList, groupTitle = '') => {
      if (!Array.isArray(panelsList)) return;
      
      panelsList.forEach((panel) => {
        if (panel.type === 'group' && panel.panels) {
          // Rekursiv Untergruppen durchsuchen
          extractPanelsFromGroups(panel.panels, groupTitle);
        } else {
          // Panel hinzufügen (auch wenn es keine movableBars hat)
          panels.push({
            id: panel.id,
            title: panel.title || 'Unbenanntes Panel',
            groupTitle: groupTitle || 'Unbenannte Gruppe',
            taskCount: (panel.movableBars && Array.isArray(panel.movableBars)) ? panel.movableBars.length : 0
          });
        }
      });
    };
    
    containerData.groups.forEach(group => {
      extractPanelsFromGroups(group.panels || [], group.title || 'Unbenannte Gruppe');
    });
    
    return panels;
  };

  // Funktion zum Abrufen ausgewählter Tasks mit vollständigen Daten
  const getSelectedTasks = () => {
    const allTasks = getAllTasks();
    const selectedIds = batchOperations.getSelectedTaskIds();
    
    return allTasks.filter(task => selectedIds.includes(task.id));
  };

  // Funktion zum Aktualisieren mehrerer Tasks
  const updateBatchTasks = useCallback((taskIds, updates) => {
    if (!panelGroupsContainerRef.current) return;

    try {
      const containerData = panelGroupsContainerRef.current.getData();
      if (!containerData || !containerData.groups) return;

      let hasChangesLocal = false;

      // Iteriere durch alle Gruppen und Panels
      const updateTaskInPanel = (panel) => {
        if (panel.movableBars && Array.isArray(panel.movableBars)) {
          panel.movableBars.forEach(bar => {
            const taskId = `${panel.id}_${bar.id}`;
            if (taskIds.includes(taskId)) {
              // Task gefunden - Update die Eigenschaften
              Object.keys(updates).forEach(key => {
                switch (key) {
                  case 'label':
                    bar.label = updates[key];
                    break;
                  case 'textColor':
                    bar.textColor = updates[key];
                    break;
                  case 'backgroundColor':
                    bar.backgroundColor = updates[key];
                    break;
                  case 'borderStyle':
                    bar.borderStyle = updates[key];
                    break;
                  case 'borderColor':
                    bar.borderColor = updates[key];
                    break;
                  case 'borderThickness':
                    bar.borderThickness = updates[key];
                    break;
                  case 'startDate':
                    bar.startDate = updates[key];
                    break;
                  case 'endDate':
                    bar.endDate = updates[key];
                    break;
                  case 'linkEvidence':
                    bar.linkEvidence = updates[key];
                    break;
                  case 'evidenceLabel':
                    bar.evidenceLabel = updates[key];
                    break;
                  case 'evidenceDueDays':
                    bar.evidenceDueDays = updates[key];
                    break;
                  case 'evidenceType':
                    bar.evidenceType = updates[key];
                    break;
                  case 'klassensynchronisation':
                    bar.klassensynchronisation = updates[key];
                    break;
                  default:
                    // Unbekannte Eigenschaft ignorieren
                    break;
                }
              });
              hasChangesLocal = true;
            }
          });
        }
        
        // Rekursiv für Untergruppen
        if (panel.type === 'group' && panel.panels) {
          panel.panels.forEach(updateTaskInPanel);
        }
      };

      containerData.groups.forEach(group => {
        if (group.panels) {
          group.panels.forEach(updateTaskInPanel);
        }
      });

      if (hasChangesLocal) {
        // Aktualisiere die Container-Daten
        panelGroupsContainerRef.current.setData(containerData);
        
        console.log(`Batch update applied to ${taskIds.length} tasks:`, updates);
      }
    } catch (error) {
      console.error('Error updating batch tasks:', error);
    }
  }, []);

  // Funktion zum Löschen mehrerer Tasks
  const deleteBatchTasks = useCallback((taskIds) => {
    if (!panelGroupsContainerRef.current) return;

    try {
      const containerData = panelGroupsContainerRef.current.getData();
      if (!containerData || !containerData.groups) return;

      let deletedCount = 0;

      // Iteriere durch alle Gruppen und Panels
      const deleteTasksFromPanel = (panel) => {
        if (panel.movableBars && Array.isArray(panel.movableBars)) {
          panel.movableBars = panel.movableBars.filter(bar => {
            const taskId = `${panel.id}_${bar.id}`;
            const shouldDelete = taskIds.includes(taskId);
            if (shouldDelete) {
              deletedCount++;
            }
            return !shouldDelete;
          });
        }
        
        // Rekursiv für Untergruppen
        if (panel.type === 'group' && panel.panels) {
          panel.panels.forEach(deleteTasksFromPanel);
        }
      };

      containerData.groups.forEach(group => {
        if (group.panels) {
          group.panels.forEach(deleteTasksFromPanel);
        }
      });

      if (deletedCount > 0) {
        // Aktualisiere die Container-Daten
        panelGroupsContainerRef.current.setData(containerData);
        
        console.log(`Batch delete: ${deletedCount} tasks deleted`);
      }
    } catch (error) {
      console.error('Error deleting batch tasks:', error);
    }
  }, []);

  // Funktion zum Zuweisen/Entfernen von Lernenden zu/von Tasks
  const assignStudentsToTasks = useCallback((taskIds, settings) => {
    try {
      const containerData = panelGroupsContainerRef.current.getData();
      let updatedCount = 0;

      const updateTasksInPanel = (panel) => {
        if (panel.movableBars) {
          panel.movableBars.forEach(bar => {
            const taskId = `${panel.id}_${bar.id}`;
            if (taskIds.includes(taskId)) {
              const { selectedLernendeIds, lernendeData, actionMode, klassensynchronisation } = settings;
              
              // Initialisiere zugewiesene Lernende falls nicht vorhanden
              if (!bar.assignedStudents) {
                bar.assignedStudents = [];
              }

              if (actionMode === 'add') {
                // Füge neue Lernende hinzu (vermeide Duplikate)
                const existingIds = new Set(bar.assignedStudents.map(s => s.id));
                const newStudents = lernendeData
                  .filter(lernender => selectedLernendeIds.includes(lernender.id) && !existingIds.has(lernender.id));
                
                bar.assignedStudents.push(...newStudents);
                
                // Füge Klassensynchronisation hinzu/aktualisiere sie
                if (klassensynchronisation) {
                  bar.klassensynchronisation = klassensynchronisation;
                }
              } else if (actionMode === 'remove') {
                // Entferne ausgewählte Lernende
                bar.assignedStudents = bar.assignedStudents.filter(
                  student => !selectedLernendeIds.includes(student.id)
                );
              }
              
              updatedCount++;
            }
          });
        }
        
        // Rekursiv für Untergruppen
        if (panel.type === 'group' && panel.panels) {
          panel.panels.forEach(updateTasksInPanel);
        }
      };

      containerData.groups.forEach(group => {
        if (group.panels) {
          group.panels.forEach(updateTasksInPanel);
        }
      });

      if (updatedCount > 0) {
        // Aktualisiere die Container-Daten
        panelGroupsContainerRef.current.setData(containerData);
        
        const actionText = settings.actionMode === 'add' ? 'zugewiesen' : 'entfernt';
        const studentCount = settings.selectedLernendeIds.length;
        console.log(`Batch student assignment: ${studentCount} Lernende ${actionText} zu ${updatedCount} Tasks`);
      }
    } catch (error) {
      console.error('Error assigning students to batch tasks:', error);
    }
  }, []);

  // Funktion zum Kopieren/Verschieben von Tasks
  const copyMoveTasksToPanel = useCallback((taskIds, settings) => {
    try {
      // Tiefes Klonen der Daten, damit Mutationen nicht das Live-State berühren
      // und setData eine echte Änderung erkennt.
      const rawData = panelGroupsContainerRef.current.getData();
      const containerData = JSON.parse(JSON.stringify(rawData));
      const { targetPanelId, createCopy } = settings;
      
      // Sammle alle zu kopierenden/verschiebenden Tasks
      const tasksToProcess = [];
      
      const collectTasksFromPanel = (panel) => {
        if (panel.movableBars) {
          panel.movableBars.forEach((bar, index) => {
            const taskId = `${panel.id}_${bar.id}`;
            if (taskIds.includes(taskId)) {
              tasksToProcess.push({
                originalBar: bar,
                sourcePanel: panel,
                sourceIndex: index,
                taskId
              });
            }
          });
        }
        
        // Rekursiv für Untergruppen
        if (panel.type === 'group' && panel.panels) {
          panel.panels.forEach(collectTasksFromPanel);
        }
      };

      containerData.groups.forEach(group => {
        if (group.panels) {
          group.panels.forEach(collectTasksFromPanel);
        }
      });

      // Finde das Ziel-Panel
      let targetPanel = null;
      
      const findTargetPanel = (panel) => {
        if (panel.id === targetPanelId) {
          targetPanel = panel;
          return;
        }
        
        if (panel.type === 'group' && panel.panels) {
          panel.panels.forEach(findTargetPanel);
        }
      };

      containerData.groups.forEach(group => {
        if (group.panels) {
          group.panels.forEach(findTargetPanel);
        }
      });

      if (!targetPanel || tasksToProcess.length === 0) {
        console.error('Target panel not found or no tasks to process');
        return;
      }

      // Initialisiere movableBars falls nicht vorhanden
      if (!targetPanel.movableBars) {
        targetPanel.movableBars = [];
      }

      let processedCount = 0;

      // Verarbeite alle Tasks (erst hinzufügen, dann entfernen)
      const tasksToRemove = [];
      
      tasksToProcess.forEach(({ originalBar, sourcePanel, sourceIndex }) => {

        
        // Erstelle eine neue Task (Kopie oder für Verschieben)
        // Task klonen und neue ID zuweisen
        const newBar = {
          ...originalBar,
          id: `bar_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`, // Neue eindeutige ID
          // UUID nur bei Kopien generieren, beim Verschieben die ursprüngliche UUID beibehalten
          uuid: createCopy ? `task-${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 18)}` : originalBar.uuid,
          assignedStudents: [] // Wird aus der DB geladen falls kopiert
        };

        // Kopie kennzeichnen
        if (createCopy && originalBar.label) {
          newBar.label = `${originalBar.label} (Kopie)`;
        }

        // Bei Kopieren: Zuordnungen von der ursprünglichen UUID zur neuen UUID kopieren
        if (createCopy && originalBar.uuid && newBar.uuid) {
          // Asynchrones Kopieren der Zuordnungen (nach dem Hinzufügen der Task)
          setTimeout(async () => {
            try {
              const userId = localStorage.getItem('userId') || loginData.user?.username || loginData.user?.login || 'default-user';
              // Verwende planMetadata aus Context statt localStorage
              const currentPlanMetadata = planMetadata;
              

              
              if (userId && currentPlanMetadata.planName) {
                const copyResult = await copyTaskAssignmentsToNewUUID(
                  userId,
                  currentPlanMetadata.planName,
                  currentPlanMetadata.planVersion || 1,
                  originalBar.uuid, // Original UUID
                  newBar.uuid // Neue UUID
                );
                
                if (copyResult.success) {
                  
                  // Lade die kopierten Zuordnungen und aktualisiere das Panel
                  const assignmentsResult = await fetchTaskAssignments(
                    userId,
                    newBar.uuid,
                    currentPlanMetadata.planVersion || 1
                  );
                  
                  if (assignmentsResult && !assignmentsResult.error) {
                    // Aktualisiere die Task im Panel mit den geladenen Zuordnungen
                    const updatedData = JSON.parse(JSON.stringify(panelGroupsContainerRef.current.getData()));
                    const updateTask = (panel) => {
                      if (panel.movableBars) {
                        panel.movableBars.forEach(bar => {
                          if (bar.uuid === newBar.uuid) {
                            bar.assignedStudents = assignmentsResult.assignments || [];
                          }
                        });
                      }
                      
                      if (panel.type === 'group' && panel.panels) {
                        panel.panels.forEach(updateTask);
                      }
                    };
                    
                    updatedData.groups.forEach(group => {
                      if (group.panels) {
                        group.panels.forEach(updateTask);
                      }
                    });
                    
                    panelGroupsContainerRef.current.setData(updatedData);
                  }
                } else {
                  console.warn('Fehler beim Kopieren der Zuordnungen:', copyResult.message || copyResult.error);
                }
              } else {
                
                // Fallback: Versuche Plan-Daten aus anderen Quellen zu bekommen
                const altUserId = userId || localStorage.getItem('username') || localStorage.getItem('currentUser') || 'default-user';
                let altPlanName = null;
                let altPlanVersion = 1;
                
                // Versuche verschiedene localStorage Keys
                const possiblePlanKeys = ['currentPlan', 'activePlan', 'selectedPlan', 'planName'];
                for (const key of possiblePlanKeys) {
                  const value = localStorage.getItem(key);
                  if (value) {
                    try {
                      const parsed = JSON.parse(value);
                      if (parsed.name || parsed.planName) {
                        altPlanName = parsed.name || parsed.planName;
                        altPlanVersion = parsed.version || parsed.planVersion || 1;
                        break;
                      }
                    } catch {
                      // Falls es kein JSON ist, verwende den Wert direkt
                      altPlanName = value;
                      break;
                    }
                  }
                }
                
                // Prüfe auch planMetadata unter verschiedenen Keys
                if (!altPlanName && currentPlanMetadata) {
                  altPlanName = currentPlanMetadata.name || currentPlanMetadata.title || currentPlanMetadata.planTitle || currentPlanMetadata.planName;
                  altPlanVersion = currentPlanMetadata.version || currentPlanMetadata.planVersion || 1;
                }
                
                if (altUserId && altPlanName) {
                  const copyResult = await copyTaskAssignmentsToNewUUID(
                    altUserId,
                    altPlanName,
                    altPlanVersion,
                    originalBar.uuid,
                    newBar.uuid
                  );
                  
                  if (copyResult.success) {
                    
                    const assignmentsResult = await fetchTaskAssignments(
                      altUserId,
                      newBar.uuid,
                      altPlanVersion
                    );
                    
                    if (assignmentsResult && !assignmentsResult.error) {
                      // Aktualisiere die Task im Panel mit den geladenen Zuordnungen
                      const updatedData = JSON.parse(JSON.stringify(panelGroupsContainerRef.current.getData()));
                      const updateTask = (panel) => {
                        if (panel.movableBars) {
                          panel.movableBars.forEach(bar => {
                            if (bar.uuid === newBar.uuid) {
                              bar.assignedStudents = assignmentsResult.assignments || [];
                            }
                          });
                        }
                        
                        if (panel.type === 'group' && panel.panels) {
                          panel.panels.forEach(updateTask);
                        }
                      };
                      
                      updatedData.groups.forEach(group => {
                        if (group.panels) {
                          group.panels.forEach(updateTask);
                        }
                      });
                      
                      panelGroupsContainerRef.current.setData(updatedData);
                    }
                  } else {
                    console.warn('Fallback: Fehler beim Kopieren der Zuordnungen:', copyResult.message || copyResult.error);
                  }
                } else {
                  
                  // Absoluter Fallback: Kopiere assignedStudents lokal
                  if (originalBar.assignedStudents && originalBar.assignedStudents.length > 0) {
                    const updatedData = JSON.parse(JSON.stringify(panelGroupsContainerRef.current.getData()));
                    const updateTask = (panel) => {
                      if (panel.movableBars) {
                        panel.movableBars.forEach(bar => {
                          if (bar.uuid === newBar.uuid) {
                            bar.assignedStudents = [...originalBar.assignedStudents];
                            console.log(`Task ${bar.id} mit UUID ${bar.uuid} lokal aktualisiert: ${bar.assignedStudents.length} Zuordnungen`);
                          }
                        });
                      }
                      
                      if (panel.type === 'group' && panel.panels) {
                        panel.panels.forEach(updateTask);
                      }
                    };
                    
                    updatedData.groups.forEach(group => {
                      if (group.panels) {
                        group.panels.forEach(updateTask);
                      }
                    });
                    
                    panelGroupsContainerRef.current.setData(updatedData);
                  }
                }
              }
            } catch (error) {
              console.error('Fehler beim Kopieren der Task-Zuordnungen:', error);
            }
          }, 500); // 500ms Verzögerung um sicherzustellen, dass die Task erst hinzugefügt wurde
        } else {
          // Fallback: Kopiere assignedStudents lokal, falls keine UUID vorhanden
          if (createCopy && originalBar.assignedStudents && originalBar.assignedStudents.length > 0) {
            newBar.assignedStudents = [...originalBar.assignedStudents];
          }
        }

        // Berechne Y-Position für das neue Panel
        const existingYPositions = targetPanel.movableBars.map(bar => bar.y || 0);
        const maxY = existingYPositions.length > 0 ? Math.max(...existingYPositions) : 0;
        newBar.y = maxY + 50 + (processedCount * 10); // 50px Abstand + 10px pro Task

        // Füge Task zum Ziel-Panel hinzu
        targetPanel.movableBars.push(newBar);
        processedCount++;

        // Merke Task zum Entfernen (bei Verschieben)
        if (!createCopy) {
          tasksToRemove.push({ sourcePanel, originalBar });
        }
      });

      // Entferne Original-Tasks bei Verschieben (rückwärts, um Indizes korrekt zu halten)
      if (!createCopy) {
        tasksToRemove.forEach(({ sourcePanel, originalBar }) => {
          const index = sourcePanel.movableBars.findIndex(bar => bar.id === originalBar.id);
          if (index !== -1) {
            sourcePanel.movableBars.splice(index, 1);
          }
        });
      }

      if (processedCount > 0) {
        // Aktualisiere die Container-Daten
        panelGroupsContainerRef.current.setData(containerData);
        
        const actionText = createCopy ? 'kopiert' : 'verschoben';
        console.log(`Batch ${actionText}: ${processedCount} Tasks ${actionText} zu Panel ${targetPanelId}`);
      }
    } catch (error) {
      console.error('Error copying/moving batch tasks:', error);
    }
  }, [planMetadata, loginData.user]);

  // Batch Operations Daten
  const batchOperationsData = {
    showBatchModal: batchOperations.showBatchModal,
    setShowBatchModal: batchOperations.setShowBatchModal,
    selectedTasks: getSelectedTasks(),
    handleBatchConfirm: (operationData) => {
      batchOperations.executeBatchOperation(operationData, updateBatchTasks, deleteBatchTasks, assignStudentsToTasks, copyMoveTasksToPanel);
    },
    allTasks: getAllTasks(),
    allPanels: getAllPanels()
  };

  // Die Panel-Zustandsverwaltung erfolgt jetzt direkt in den CollapsiblePanel Komponenten über Events

  return (
    <EditingProvider user={loginData.user}>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header 
          ref={headerRef}
          header={header}
          user={loginData.user}
          onLoginOpen={loginData.handleLoginOpen}
          onLogout={loginData.handleLogout}
          undoRedo={undoRedoData}
          onToggleAllPanels={handleToggleAllPanels}
          areAllPanelsClosed={areAllPanelsClosed}
          autosaveFunction={saveLoadData.handleCtrlSave}
          silentAutosaveFunction={saveLoadData.handleSilentSave}
          onMergeDuplicateGroups={handleMergeDuplicateGroups}
          onBatchOperations={batchOperations.openBatchModal}
          selectedTasksCount={batchOperations.getSelectedCount()}
          batchOperations={batchOperations}
          hasDuplicates={hasDuplicates}
          onGlobalSync={handleGlobalSync}
          currentLoadedPlanId={saveLoadData.currentLoadedPlanId}
          planMetadata={planMetadata}
          onPdfExportOpen={() => setShowPdfDialog(true)}
        />

        <Modals 
          loginData={loginData}
          saveLoadData={saveLoadData}
          schuelerData={schuelerData}
          newPlanData={newPlanData}
          mergeData={mergeData}
          batchOperationsData={batchOperationsData}
        />

        {/* Single Task CopyMove Dialog */}
        <CopyMoveDialog
          show={copyMove.showCopyMoveDialog}
          onClose={copyMove.closeCopyMoveDialog}
          selectedTasks={[]} // Leer für Batch-Modus
          allTasks={getAllTasks()}
          allPanels={getAllPanels()} // Alle verfügbaren Panels (auch leere)
          singleTask={copyMove.singleTask}
          onConfirm={(operationData) => {
            if (operationData.isSingleTaskMode && copyMove.singleTask) {
              // Einzelne Task kopieren/verschieben
              copyMoveTasksToPanel(operationData.taskIds, operationData.settings);
            }
            copyMove.closeCopyMoveDialog();
          }}
        />

        {/* Global Sync Modal */}
        <GlobalSyncModal
          show={showGlobalSyncModal}
          onClose={() => setShowGlobalSyncModal(false)}
          onConfirm={handleGlobalSyncConfirm}
        />

        <PdfExportDialog
          isOpen={showPdfDialog}
          onClose={() => setShowPdfDialog(false)}
          planName={currentPlanName}
          onOpenAllPanels={openAllPanelsForExport}
          onRestorePanels={restorePanelsAfterExport}
        />

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          <Sidebar 
            width={resizeData.leftWidth}
            minWidth={resizeData.MIN_WIDTH}
            maxWidth={resizeData.MAX_WIDTH}
            left={left}
            visibleWeeks={visibleWeeks}
            setVisibleWeeks={setVisibleWeeks}
            onVisibleWeeksChangeStart={handleVisibleWeeksChangeStart}
            onVisibleWeeksChangeEnd={handleVisibleWeeksChangeEnd}
            weekWidth={weekWidth}
            setWeekWidth={setWeekWidth}
            onZoomChangeStart={handleZoomChangeStart}
            onZoomChangeEnd={handleZoomChangeEnd}
            hasOpenInfoPanels={hasOpenInfoPanels}
            onCloseAllInfoPanels={handleCloseAllInfoPanels}
            onLoad={saveLoadData.handleDirectFileLoad}
            onSave={saveLoadData.handleSave}
            onDatabaseSave={saveLoadData.handleDatabaseSave}
            onLoadFromDatabase={saveLoadData.handleLoadFromDatabase}
            onUpdatePlan={saveLoadData.handleUpdatePlan}
            user={loginData.user} // Übergabe des User-Objekts
            onSchuelerOpen={() => setShowSchuelerModal(true)}
            onCreateNewPlan={handleCreateNewPlan}
            currentLoadedPlanId={saveLoadData.currentLoadedPlanId}
            batchOperations={batchOperations}
            onLoadStudentPlan={handleLoadStudentPlan} // Neue Prop für Lernenden-Pläne
            startDate={startDate}
          />

          <ResizeHandle onMouseDown={resizeData.onMouseDown} />

          <CalendarStrips 
            ref={calendarStripsRef}
            onDataChange={undoRedoData.handleDataChange}
            panelGroupsContainerRef={panelGroupsContainerRef}
            visibleWeeks={visibleWeeks}
            weekWidth={weekWidth}
            isVisibleWeeksChanging={isVisibleWeeksChanging}
            isZoomChanging={isZoomChanging}
            onInfoPanelsChange={handleInfoPanelsChange}
            batchOperations={batchOperations}
            onCheckForDuplicates={checkForDuplicates}
          />
        </div>
        
      </div>
    </EditingProvider>
  );
}

// Hauptexport-Komponente mit StartDateProvider
export default function ResizableLayout(props) {
  return (
    <StartDateProvider>
      <CalendarStripsProvider>
        <ResizableLayoutContent {...props} />
      </CalendarStripsProvider>
    </StartDateProvider>
  );
}